import React, { useState, useEffect } from 'react';
import {
  X, User, FileText, Building, MapPin, Phone, Calendar, Map, DollarSign,
  Plus, Trash2, AlertCircle, Save, Briefcase, UserCheck
} from 'lucide-react';
import { MODES_PAIEMENT } from '../utils/finance';
import {
  STATUS_TRANSITIONS, ZONES_MERCHICH, REGIMES_FONCIERS,
  REGIMES_WITH_REFERENCE, NATURES_PROJETS
} from '../utils/constants';
import {
  validateCoordinates, parseCoordinatesFromText, generateId, formatNumber,
  calculateTotalAvances, calculateResteAPayer, normalizeClients, buildClientLabel
} from '../utils/helpers';

const NEW_TYPE_OPTION = '__nouveau_type__';

const emptyClient = () => ({ id: generateId(), type: 'personne', nom: '', cin: '', ice: '', telephone: '' });
const emptyRepresentant = () => ({ nom: '', cin: '', telephone: '', qualite: '' });

const ProjectForm = ({ project, onSave, onCancel, existingProjects = [], agents = [], projectTypes = [], onAddProjectType, clients: annuaire = [] }) => {
  const isEditing = !!project?.id;

  // États du formulaire
  const [formData, setFormData] = useState({
    client: '',
    cin: '',
    clients: [emptyClient()],
    representant: emptyRepresentant(),
    commune: '',
    province: '',
    regimeFoncier: '',
    regimeReference: '',
    natureProjet: '',
    agentResponsable: '',
    telephone: '',
    date: new Date().toISOString().split('T')[0],
    bet: '',
    topo: '',
    montantTotal: '',
    fraisSupplementaires: '',
    fraisSupplementairesNote: '',
    avances: [],
    paye: false,
    zoneMerchich: 'EPSG:26191',
    coordonnees: [],
    statut: 'En cours',
    statutRemarque: '',
    observations: ''
  });

  const [activeTab, setActiveTab] = useState(0);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [newNatureMode, setNewNatureMode] = useState(false);
  const [newNature, setNewNature] = useState('');

  // États coordonnées
  const [coordInputMode, setCoordInputMode] = useState('individual');
  const [newCoord, setNewCoord] = useState({ nom: '', x: '', y: '' });
  const [coordText, setCoordText] = useState('');
  const [coordWarning, setCoordWarning] = useState('');

  // État pour nouvelle avance
  const [newAvance, setNewAvance] = useState({ montant: '', date: new Date().toISOString().split('T')[0], description: '', modePaiement: 'espece', enCaisse: true, reference: '' });
  // Autocomplétion du nom de client depuis l'annuaire : { rowId, index } (ligne en cours de saisie)
  const [suggest, setSuggest] = useState(null);
  const suggestionsFor = (row) => {
    const q = (row?.nom || '').trim().toLowerCase();
    if (!q) return [];
    const used = new Set(formData.clients.map((x) => x.id !== row.id && (x.nom || '').trim().toLowerCase()).filter(Boolean));
    return annuaire
      .filter((c) => (c.nom || '').toLowerCase().includes(q) && !used.has((c.nom || '').toLowerCase()))
      .filter((c) => (c.nom || '').trim().toLowerCase() !== q || c.cin || c.ice || c.telephone)
      .sort((x, y) => ((x.type === row.type) === (y.type === row.type) ? 0 : x.type === row.type ? -1 : 1))
      .slice(0, 6);
  };

  // Initialiser avec les données du projet
  useEffect(() => {
    if (project) {
      setFormData({
        client: project.client || '',
        cin: project.cin || '',
        clients: normalizeClients(project),
        representant: { ...emptyRepresentant(), ...(project.representant || {}) },
        commune: project.commune || '',
        province: project.province || '',
        regimeFoncier: project.regimeFoncier || '',
        regimeReference: project.regimeReference || '',
        natureProjet: project.natureProjet || '',
        agentResponsable: project.agentResponsable || '',
        telephone: project.telephone || project.telIntermediaire || '',
        date: project.date || new Date().toISOString().split('T')[0],
        bet: project.bet || '',
        topo: project.topo || '',
        montantTotal: project.montantTotal?.toString() || '',
        fraisSupplementaires: project.fraisSupplementaires?.toString() || '',
        fraisSupplementairesNote: project.fraisSupplementairesNote || '',
        avances: project.avances || [],
        paye: project.paye || false,
        zoneMerchich: project.zoneMerchich || 'EPSG:26191',
        coordonnees: project.coordonnees || [],
        statut: project.statut || 'En cours',
        statutRemarque: '',
        observations: project.observations || ''
      });
    }
  }, [project]);

  // Validation du formulaire
  const validateForm = () => {
    const newErrors = {};

    const namedClients = formData.clients.filter(c => (c.nom || '').trim());
    if (namedClients.length === 0) {
      newErrors.client = 'Au moins un client avec un nom est requis';
    }

    if (parseFloat(formData.fraisSupplementaires) > 0 && !formData.fraisSupplementairesNote.trim()) {
      newErrors.fraisSupplementairesNote = 'Précisez à quoi correspondent ces frais';
    }

    if (isEditing && formData.statut === 'Défavorable' && (project?.statut || 'En cours') !== 'Défavorable' && !formData.statutRemarque.trim()) {
      newErrors.statutRemarque = 'Les remarques de la commission sont obligatoires pour un avis défavorable';
    }

    if (newNatureMode && !newNature.trim()) {
      newErrors.natureProjet = 'Saisissez le nouveau type de projet ou choisissez-en un';
    }

    // Vérifier si référence requise pour Titre/Réquisition
    if (REGIMES_WITH_REFERENCE.includes(formData.regimeFoncier) && !formData.regimeReference.trim()) {
      newErrors.regimeReference = `La référence ${formData.regimeFoncier} est requise`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Compter les erreurs par onglet
  const getTabErrorCount = (tabIndex) => {
    const tabFields = [
      ['client', 'regimeReference', 'natureProjet', 'statutRemarque'], // Onglet 0
      ['fraisSupplementairesNote'], // Onglet 1
      [], // Onglet 2
      []  // Onglet 3
    ];
    return tabFields[tabIndex]?.filter(field => errors[field]).length || 0;
  };

  // Gérer les changements
  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };

      // Si on change le régime foncier et qu'il n'est plus Titre/Réquisition, vider la référence
      if (field === 'regimeFoncier' && !REGIMES_WITH_REFERENCE.includes(value)) {
        newData.regimeReference = '';
      }

      return newData;
    });
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Ajouter une avance
  const addAvance = () => {
    if (!newAvance.montant || parseFloat(newAvance.montant) <= 0) {
      return;
    }

    const avance = {
      id: generateId(),
      montant: parseFloat(newAvance.montant),
      date: newAvance.date,
      description: newAvance.description,
      modePaiement: newAvance.modePaiement || 'espece',
      enCaisse: newAvance.enCaisse === true,
      reference: newAvance.reference || '',
      createdAt: new Date().toISOString()
    };

    setFormData(prev => ({
      ...prev,
      avances: [...prev.avances, avance]
    }));

    setNewAvance({ montant: '', date: new Date().toISOString().split('T')[0], description: '', modePaiement: 'espece', enCaisse: true, reference: '' });
  };

  // Reprendre un client de l'annuaire dans la ligne en cours de saisie (type, nom, CIN/ICE, téléphone)
  const pickFromAnnuaire = (rowId, c) => {
    const rec = { type: c.type === 'societe' ? 'societe' : 'personne', nom: c.nom || '', cin: c.cin || '', ice: c.ice || '', telephone: c.telephone || '' };
    setFormData(prev => ({ ...prev, clients: prev.clients.map((x) => (x.id === rowId ? { ...x, ...rec } : x)) }));
    setSuggest(null);
    if (errors.client) setErrors(prev => { const e = { ...prev }; delete e.client; return e; });
  };

  const handleClientNameKeyDown = (e, row) => {
    const list = suggest?.rowId === row.id ? suggestionsFor(row) : [];
    if (!list.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setSuggest({ rowId: row.id, index: (suggest.index + 1) % list.length }); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSuggest({ rowId: row.id, index: (suggest.index - 1 + list.length) % list.length }); }
    else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pickFromAnnuaire(row.id, list[suggest.index] || list[0]); }
    else if (e.key === 'Escape') { setSuggest(null); }
  };

  // Supprimer une avance
  const removeAvance = (avanceId) => {
    setFormData(prev => ({
      ...prev,
      avances: prev.avances.filter(a => a.id !== avanceId)
    }));
  };

  // Ajouter une coordonnée
  const addCoordinate = () => {
    if (!newCoord.x || !newCoord.y) {
      setCoordWarning('Veuillez saisir les coordonnées X et Y');
      return;
    }

    const validation = validateCoordinates(newCoord.x, newCoord.y, formData.zoneMerchich);
    if (!validation.valid) {
      setCoordWarning(validation.message);
    }

    const coord = {
      id: generateId(),
      nom: newCoord.nom || `Point ${formData.coordonnees.length + 1}`,
      x: parseFloat(newCoord.x),
      y: parseFloat(newCoord.y)
    };

    setFormData(prev => ({
      ...prev,
      coordonnees: [...prev.coordonnees, coord]
    }));

    setNewCoord({ nom: '', x: '', y: '' });
    setCoordWarning('');
  };

  // Ajouter coordonnées en lot
  const addCoordinatesFromText = () => {
    const coords = parseCoordinatesFromText(coordText);
    if (coords.length === 0) {
      setCoordWarning('Aucune coordonnée valide trouvée. Format: Nom,X,Y');
      return;
    }

    let hasWarnings = false;
    coords.forEach(coord => {
      const validation = validateCoordinates(coord.x, coord.y, formData.zoneMerchich);
      if (!validation.valid) {
        hasWarnings = true;
      }
    });

    if (hasWarnings) {
      setCoordWarning('Certaines coordonnées sont hors des limites de la zone');
    }

    setFormData(prev => ({
      ...prev,
      coordonnees: [...prev.coordonnees, ...coords]
    }));

    setCoordText('');
  };

  // Supprimer une coordonnée
  const removeCoordinate = (id) => {
    setFormData(prev => ({
      ...prev,
      coordonnees: prev.coordonnees.filter(c => c.id !== id)
    }));
  };

  // Clients (plusieurs par projet)
  const updateClient = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      clients: prev.clients.map(c => (c.id === id ? { ...c, [field]: value } : c))
    }));
    if (errors.client) setErrors(prev => { const e = { ...prev }; delete e.client; return e; });
  };
  const addClient = () => setFormData(prev => ({ ...prev, clients: [...prev.clients, emptyClient()] }));
  const removeClient = (id) => setFormData(prev => ({
    ...prev,
    clients: prev.clients.length > 1 ? prev.clients.filter(c => c.id !== id) : [emptyClient()]
  }));
  const updateRepresentant = (field, value) => setFormData(prev => ({
    ...prev,
    representant: { ...prev.representant, [field]: value }
  }));

  // Nature du projet : nouveau type saisi par l'utilisateur
  const handleNatureSelect = (value) => {
    if (value === NEW_TYPE_OPTION) {
      setNewNatureMode(true);
      setNewNature('');
      handleChange('natureProjet', '');
    } else {
      setNewNatureMode(false);
      handleChange('natureProjet', value);
    }
  };

  // Sauvegarder
  const handleSubmit = async () => {
    if (!validateForm()) {
      const tabWithError = [0, 1, 2, 3].find(i => getTabErrorCount(i) > 0);
      if (tabWithError !== undefined) setActiveTab(tabWithError);
      return;
    }

    setLoading(true);

    const clients = formData.clients
      .filter(c => (c.nom || '').trim())
      .map(c => ({
        id: c.id || generateId(),
        type: c.type === 'societe' ? 'societe' : 'personne',
        nom: c.nom.trim(),
        cin: c.type === 'societe' ? '' : (c.cin || '').trim(),
        ice: c.type === 'societe' ? (c.ice || '').trim() : '',
        telephone: (c.telephone || '').trim()
      }));
    const firstPersonne = clients.find(c => c.type === 'personne' && c.cin);

    let natureProjet = formData.natureProjet;
    if (newNatureMode && newNature.trim()) {
      natureProjet = newNature.trim();
      if (onAddProjectType && !projectTypes.includes(natureProjet)) {
        await onAddProjectType(natureProjet);
      }
    }

    const dataToSave = {
      ...formData,
      natureProjet,
      clients,
      // Compatibilité : champs historiques utilisés par la liste, la recherche et le reçu
      client: buildClientLabel(clients),
      cin: firstPersonne ? firstPersonne.cin : '',
      representant: (formData.representant?.nom || '').trim() ? {
        nom: formData.representant.nom.trim(),
        cin: (formData.representant.cin || '').trim(),
        telephone: (formData.representant.telephone || '').trim(),
        qualite: (formData.representant.qualite || '').trim()
      } : null,
      montantTotal: parseFloat(formData.montantTotal) || 0,
      fraisSupplementaires: parseFloat(formData.fraisSupplementaires) || 0
    };

    try {
      await onSave(dataToSave);
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
    }

    setLoading(false);
  };

  // Obtenir les statuts disponibles
  const getAvailableStatuses = () => {
    if (!isEditing) return ['En cours'];
    const currentStatus = formData.statut;
    const nextStatuses = STATUS_TRANSITIONS[currentStatus] || [];
    return [currentStatus, ...nextStatuses];
  };

  // Tabs configuration
  const tabs = [
    { name: 'Informations Générales', icon: User },
    { name: 'Financier', icon: DollarSign },
    { name: 'Coordonnées', icon: MapPin },
    { name: 'Observations', icon: FileText }
  ];

  // Calculs financiers
  const totalGeneral = (parseFloat(formData.montantTotal) || 0) + (parseFloat(formData.fraisSupplementaires) || 0);
  const totalAvances = calculateTotalAvances(formData.avances);
  const resteAPayer = calculateResteAPayer(formData.montantTotal, formData.avances, formData.fraisSupplementaires);

  return (
    <div className="modal-scrim" style={{ zIndex: 9999 }}>
      <div className="modal-panel max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-bold text-slate-900">
            {isEditing ? `Modifier l'Affaire - ${project.refGlobal}` : 'Nouvelle Affaire'}
          </h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
          >
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        {/* Tabs Navigation */}
        <div className="flex border-b border-slate-200 px-4 sm:px-6 overflow-x-auto no-scrollbar">
          {tabs.map((tab, index) => {
            const Icon = tab.icon;
            const errorCount = getTabErrorCount(index);
            return (
              <button
                key={index}
                onClick={() => setActiveTab(index)}
                className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap relative ${
                  activeTab === index
                    ? 'border-blue-500 text-brand-700'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <Icon className="h-4 w-4 mr-2" />
                {tab.name}
                {errorCount > 0 && (
                  <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5">
                    {errorCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
          {/* Tab 0: Informations Générales */}
          {activeTab === 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Clients */}
              <div className="md:col-span-2 lg:col-span-3">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-slate-700">
                    Client(s) <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={addClient}
                    className="flex items-center text-xs px-2.5 py-1.5 bg-brand-50 text-brand-700 rounded-lg hover:bg-brand-100 transition-colors"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Ajouter un client
                  </button>
                </div>
                <div className="space-y-2">
                  {formData.clients.map((c, index) => (
                    <div key={c.id} className="grid grid-cols-1 sm:grid-cols-12 gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3">
                      <div className="sm:col-span-3">
                        <select
                          value={c.type}
                          onChange={(e) => updateClient(c.id, 'type', e.target.value)}
                          className="field"
                        >
                          <option value="personne">Personne physique</option>
                          <option value="societe">Société</option>
                        </select>
                      </div>
                      <div className="sm:col-span-4 relative">
                        {c.type === 'societe'
                          ? <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          : <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />}
                        <input
                          type="text"
                          value={c.nom}
                          onChange={(e) => { updateClient(c.id, 'nom', e.target.value); setSuggest({ rowId: c.id, index: 0 }); }}
                          onFocus={() => setSuggest({ rowId: c.id, index: 0 })}
                          onBlur={() => setTimeout(() => setSuggest((s) => (s?.rowId === c.id ? null : s)), 150)}
                          onKeyDown={(e) => handleClientNameKeyDown(e, c)}
                          className={`w-full pl-10 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-brand-500 ${
                            errors.client && index === 0 ? 'border-red-500' : 'border-slate-300'
                          }`}
                          placeholder={c.type === 'societe' ? 'Raison sociale' : 'Nom et prénom'}
                          autoComplete="off"
                          role="combobox"
                          aria-autocomplete="list"
                          aria-expanded={suggest?.rowId === c.id && suggestionsFor(c).length > 0}
                          aria-controls={`client-suggest-${c.id}`}
                        />
                        {suggest?.rowId === c.id && suggestionsFor(c).length > 0 && (
                          <ul id={`client-suggest-${c.id}`} className="absolute z-30 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-card-hover overflow-hidden" role="listbox" aria-label="Clients de l'annuaire">
                            {suggestionsFor(c).map((s, i) => (
                              <li key={s.id}>
                                <button
                                  type="button"
                                  role="option"
                                  aria-selected={i === suggest.index}
                                  onMouseDown={(e) => { e.preventDefault(); pickFromAnnuaire(c.id, s); }}
                                  className={`w-full text-left px-3 py-2.5 min-h-[44px] flex items-center gap-2 ${i === suggest.index ? 'bg-brand-600 text-white' : 'hover:bg-brand-50'}`}
                                >
                                  {s.type === 'societe' ? <Briefcase className="h-4 w-4 flex-shrink-0 opacity-70" /> : <User className="h-4 w-4 flex-shrink-0 opacity-70" />}
                                  <span className="text-sm truncate">{s.nom}</span>
                                  <span className={`text-xs ml-auto whitespace-nowrap ${i === suggest.index ? 'text-brand-100' : 'text-slate-500'}`}>{s.type === 'societe' ? (s.ice ? `ICE ${s.ice}` : 'Société') : (s.cin ? `CIN ${s.cin}` : 'Personne')}</span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div className="sm:col-span-2 relative">
                        <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        {c.type === 'societe' ? (
                          <input
                            type="text"
                            value={c.ice}
                            onChange={(e) => updateClient(c.id, 'ice', e.target.value)}
                            className="field pl-10"
                            placeholder="ICE"
                          />
                        ) : (
                          <input
                            type="text"
                            value={c.cin}
                            onChange={(e) => updateClient(c.id, 'cin', e.target.value)}
                            className="field pl-10"
                            placeholder="CIN"
                          />
                        )}
                      </div>
                      <div className="sm:col-span-2 relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                          type="tel"
                          value={c.telephone}
                          onChange={(e) => updateClient(c.id, 'telephone', e.target.value)}
                          className="field pl-10"
                          placeholder="Téléphone"
                        />
                      </div>
                      <div className="sm:col-span-1 flex items-center justify-end">
                        <button
                          type="button"
                          onClick={() => removeClient(c.id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Retirer ce client"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {errors.client && (
                  <p className="mt-1 text-sm text-red-500 flex items-center">
                    <AlertCircle className="h-3 w-3 mr-1" />{errors.client}
                  </p>
                )}
              </div>

              {/* Représentant du client */}
              <div className="md:col-span-2 lg:col-span-3">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Représentant du client <span className="text-slate-400 font-normal">(optionnel)</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="relative">
                    <UserCheck className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="text"
                      value={formData.representant.nom}
                      onChange={(e) => updateRepresentant('nom', e.target.value)}
                      className="field pl-10"
                      placeholder="Nom du représentant"
                    />
                  </div>
                  <input
                    type="text"
                    value={formData.representant.qualite}
                    onChange={(e) => updateRepresentant('qualite', e.target.value)}
                    className="field"
                    placeholder="Qualité (gérant, mandataire...)"
                  />
                  <input
                    type="text"
                    value={formData.representant.cin}
                    onChange={(e) => updateRepresentant('cin', e.target.value)}
                    className="field"
                    placeholder="CIN"
                  />
                  <input
                    type="tel"
                    value={formData.representant.telephone}
                    onChange={(e) => updateRepresentant('telephone', e.target.value)}
                    className="field"
                    placeholder="Téléphone"
                  />
                </div>
              </div>

              {/* Commune */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Commune</label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    value={formData.commune}
                    onChange={(e) => handleChange('commune', e.target.value)}
                    className="field pl-10"
                    placeholder="Commune"
                  />
                </div>
              </div>

              {/* Province */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Province</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    value={formData.province}
                    onChange={(e) => handleChange('province', e.target.value)}
                    className="field pl-10"
                    placeholder="Province"
                  />
                </div>
              </div>

              {/* Régime Foncier */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Régime Foncier</label>
                <select
                  value={formData.regimeFoncier}
                  onChange={(e) => handleChange('regimeFoncier', e.target.value)}
                  className="field"
                >
                  <option value="">Sélectionner...</option>
                  {REGIMES_FONCIERS.map(regime => (
                    <option key={regime} value={regime}>{regime}</option>
                  ))}
                </select>
              </div>

              {/* Référence Titre/Réquisition */}
              {REGIMES_WITH_REFERENCE.includes(formData.regimeFoncier) && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Référence {formData.regimeFoncier} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.regimeReference}
                    onChange={(e) => handleChange('regimeReference', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 ${
                      errors.regimeReference ? 'border-red-500' : 'border-slate-300'
                    }`}
                    placeholder={`N° ${formData.regimeFoncier}`}
                  />
                  {errors.regimeReference && (
                    <p className="mt-1 text-sm text-red-500 flex items-center">
                      <AlertCircle className="h-3 w-3 mr-1" />{errors.regimeReference}
                    </p>
                  )}
                </div>
              )}

              {/* Nature du Projet */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nature du Projet</label>
                <select
                  value={newNatureMode ? NEW_TYPE_OPTION : formData.natureProjet}
                  onChange={(e) => handleNatureSelect(e.target.value)}
                  className="field"
                >
                  <option value="">Sélectionner...</option>
                  {(projectTypes.length > 0 ? projectTypes : NATURES_PROJETS).map(nature => (
                    <option key={nature} value={nature}>{nature}</option>
                  ))}
                  {formData.natureProjet && !newNatureMode
                    && !(projectTypes.length > 0 ? projectTypes : NATURES_PROJETS).includes(formData.natureProjet) && (
                    <option value={formData.natureProjet}>{formData.natureProjet}</option>
                  )}
                  <option value={NEW_TYPE_OPTION}>+ Nouveau type de projet...</option>
                </select>
                {newNatureMode && (
                  <input
                    type="text"
                    autoFocus
                    value={newNature}
                    onChange={(e) => {
                      setNewNature(e.target.value);
                      if (errors.natureProjet) setErrors(prev => { const n = { ...prev }; delete n.natureProjet; return n; });
                    }}
                    className={`mt-2 w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 ${
                      errors.natureProjet ? 'border-red-500' : 'border-brand-300'
                    }`}
                    placeholder="Nom du nouveau type (sera ajouté à votre liste)"
                  />
                )}
                {errors.natureProjet && (
                  <p className="mt-1 text-sm text-red-500 flex items-center">
                    <AlertCircle className="h-3 w-3 mr-1" />{errors.natureProjet}
                  </p>
                )}
                <p className="mt-1 text-xs text-slate-500">
                  Gérez la liste dans Profil → Types de projet
                </p>
              </div>

              {/* Agent Responsable - Dropdown */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Agent Responsable</label>
                <select
                  value={formData.agentResponsable}
                  onChange={(e) => handleChange('agentResponsable', e.target.value)}
                  className="field"
                >
                  <option value="">Sélectionner un agent...</option>
                  {agents.map(agent => (
                    <option key={agent} value={agent}>{agent}</option>
                  ))}
                </select>
                {agents.length === 0 && (
                  <p className="mt-1 text-xs text-slate-500">
                    Ajoutez des agents dans votre profil
                  </p>
                )}
              </div>

              {/* Téléphone */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="tel"
                    value={formData.telephone}
                    onChange={(e) => handleChange('telephone', e.target.value)}
                    className="field pl-10"
                    placeholder="Téléphone"
                  />
                </div>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleChange('date', e.target.value)}
                    className="field pl-10"
                  />
                </div>
              </div>

              {/* BET */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">BET</label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    value={formData.bet}
                    onChange={(e) => handleChange('bet', e.target.value)}
                    className="field pl-10"
                    placeholder="Bureau d'études techniques"
                  />
                </div>
              </div>

              {/* TOPO */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">TOPO</label>
                <div className="relative">
                  <Map className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    value={formData.topo}
                    onChange={(e) => handleChange('topo', e.target.value)}
                    className="field pl-10"
                    placeholder="Topographe"
                  />
                </div>
              </div>

              {/* Statut (seulement en modification) */}
              {isEditing && (
                <div className="lg:col-span-3">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Statut</label>
                  <select
                    value={formData.statut}
                    onChange={(e) => handleChange('statut', e.target.value)}
                    className="field"
                  >
                    {getAvailableStatuses().map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-slate-500">
                    Transitions possibles: {STATUS_TRANSITIONS[formData.statut]?.join(', ') || 'Aucune'}
                  </p>
                  {formData.statut !== (project?.statut || 'En cours') && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        {formData.statut === 'Défavorable' ? 'Remarques de la commission *' : 'Commentaire sur le changement de statut'}
                      </label>
                      <textarea
                        value={formData.statutRemarque}
                        onChange={(e) => handleChange('statutRemarque', e.target.value)}
                        rows={2}
                        className={`field ${errors.statutRemarque ? 'border-red-300' : ''}`}
                        placeholder={formData.statut === 'Défavorable' ? 'Motifs du refus, pièces à corriger…' : 'Optionnel'}
                        aria-required={formData.statut === 'Défavorable'}
                      />
                      {errors.statutRemarque && <p className="mt-1 text-sm text-red-600">{errors.statutRemarque}</p>}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Tab 1: Financier */}
          {activeTab === 1 && (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Montant Total */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Montant Total</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="number"
                      min="0"
                      value={formData.montantTotal}
                      onChange={(e) => handleChange('montantTotal', e.target.value)}
                      className="w-full pl-10 pr-12 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-500"
                      placeholder="0"
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 text-sm">DH</span>
                  </div>
                </div>

                {/* Frais Supplémentaires */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Frais Supplémentaires</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="number"
                      min="0"
                      value={formData.fraisSupplementaires}
                      onChange={(e) => handleChange('fraisSupplementaires', e.target.value)}
                      className="w-full pl-10 pr-12 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-500"
                      placeholder="0"
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 text-sm">DH</span>
                  </div>
                </div>
              </div>

              {/* Motif des frais supplémentaires */}
              {(parseFloat(formData.fraisSupplementaires) > 0 || formData.fraisSupplementairesNote) && (
                <div className="mb-6 mt-4">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    À quoi correspondent les frais supplémentaires ? <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.fraisSupplementairesNote}
                    onChange={(e) => handleChange('fraisSupplementairesNote', e.target.value)}
                    className={`field ${errors.fraisSupplementairesNote ? 'border-red-500' : ''}`}
                    placeholder="Ex : frais de dépôt commune, tirages de plans, déplacement…"
                  />
                  {errors.fraisSupplementairesNote && (
                    <p className="mt-1 text-sm text-red-500 flex items-center">
                      <AlertCircle className="h-3 w-3 mr-1" />{errors.fraisSupplementairesNote}
                    </p>
                  )}
                </div>
              )}

              {/* Section Avances */}
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Avances</h4>

                {/* Formulaire nouvelle avance */}
                <div className="bg-slate-50 rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Montant</label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          value={newAvance.montant}
                          onChange={(e) => setNewAvance({ ...newAvance, montant: e.target.value })}
                          className="field"
                          placeholder="0"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Date</label>
                      <input
                        type="date"
                        value={newAvance.date}
                        onChange={(e) => setNewAvance({ ...newAvance, date: e.target.value })}
                        className="field"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
                      <input
                        type="text"
                        value={newAvance.description}
                        onChange={(e) => setNewAvance({ ...newAvance, description: e.target.value })}
                        className="field"
                        placeholder="Description (optionnel)"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Mode de paiement</label>
                      <select
                        value={newAvance.modePaiement}
                        onChange={(e) => setNewAvance({ ...newAvance, modePaiement: e.target.value })}
                        className="field"
                      >
                        {MODES_PAIEMENT.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Référence (n° chèque / virement)</label>
                      <input
                        type="text"
                        value={newAvance.reference}
                        onChange={(e) => setNewAvance({ ...newAvance, reference: e.target.value })}
                        className="field"
                        placeholder="Optionnel"
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 min-h-[44px] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newAvance.enCaisse}
                          onChange={(e) => setNewAvance({ ...newAvance, enCaisse: e.target.checked })}
                          className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                        />
                        <span className="text-sm text-slate-700">Encaissé en caisse</span>
                      </label>
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={addAvance}
                        className="btn-success w-full"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Ajouter
                      </button>
                    </div>
                  </div>
                </div>

                {/* Liste des avances */}
                {formData.avances.length > 0 && (
                  <div className="border border-slate-200 rounded-lg divide-y mb-4">
                    {formData.avances.map((avance) => (
                      <div key={avance.id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50">
                        <div className="flex items-center space-x-4 text-sm">
                          <span className="font-semibold text-green-600">{formatNumber(avance.montant)} DH</span>
                          <span className="text-slate-500">{avance.date}</span>
                          {avance.description && (
                            <span className="text-slate-400">- {avance.description}</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAvance(avance.id)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Payé */}
              <div className="mt-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.paye}
                    onChange={(e) => handleChange('paye', e.target.checked)}
                    className="h-5 w-5 text-brand-700 border-slate-300 rounded focus:ring-brand-500"
                  />
                  <span className="ml-3 text-sm font-medium text-slate-700">Projet entièrement payé</span>
                </label>
              </div>

              {/* Résumé Financier */}
              <div className="mt-6 bg-brand-50 border border-brand-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-brand-800 mb-3">Résumé Financier</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-xs text-brand-700">Montant Total</p>
                    <p className="text-lg font-bold text-brand-800">{formatNumber(totalGeneral)} DH</p>
                  </div>
                  <div>
                    <p className="text-xs text-green-600">Total Avances</p>
                    <p className="text-lg font-bold text-green-700">{formatNumber(totalAvances)} DH</p>
                  </div>
                  <div>
                    <p className="text-xs text-orange-600">Reste à Payer</p>
                    <p className={`text-lg font-bold ${resteAPayer > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                      {formatNumber(resteAPayer)} DH
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-600">Nb Avances</p>
                    <p className="text-lg font-bold text-slate-800">{formData.avances.length}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tab 2: Coordonnées */}
          {activeTab === 2 && (
            <div>
              {/* Sélection Zone */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">Zone Merchich</label>
                <div className="relative">
                  <Map className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <select
                    value={formData.zoneMerchich}
                    onChange={(e) => handleChange('zoneMerchich', e.target.value)}
                    className="field pl-10"
                  >
                    {ZONES_MERCHICH.map(zone => (
                      <option key={zone.id} value={zone.id}>
                        {zone.name} - {zone.description}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Mode de saisie */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">Mode de saisie</label>
                <div className="flex space-x-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      checked={coordInputMode === 'individual'}
                      onChange={() => setCoordInputMode('individual')}
                      className="h-4 w-4 text-brand-700 border-slate-300 focus:ring-brand-500"
                    />
                    <span className="ml-2 text-sm text-slate-700">Saisie point par point</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      checked={coordInputMode === 'batch'}
                      onChange={() => setCoordInputMode('batch')}
                      className="h-4 w-4 text-brand-700 border-slate-300 focus:ring-brand-500"
                    />
                    <span className="ml-2 text-sm text-slate-700">Saisie en lot (texte)</span>
                  </label>
                </div>
              </div>

              {/* Saisie individuelle */}
              {coordInputMode === 'individual' && (
                <div className="bg-slate-50 rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Nom du point</label>
                      <input
                        type="text"
                        value={newCoord.nom}
                        onChange={(e) => setNewCoord({ ...newCoord, nom: e.target.value })}
                        className="field"
                        placeholder={`Point ${formData.coordonnees.length + 1}`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Coordonnée X</label>
                      <input
                        type="number"
                        value={newCoord.x}
                        onChange={(e) => setNewCoord({ ...newCoord, x: e.target.value })}
                        className="field"
                        placeholder="500000"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Coordonnée Y</label>
                      <input
                        type="number"
                        value={newCoord.y}
                        onChange={(e) => setNewCoord({ ...newCoord, y: e.target.value })}
                        className="field"
                        placeholder="300000"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={addCoordinate}
                        className="btn-primary w-full"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Ajouter
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Saisie en lot */}
              {coordInputMode === 'batch' && (
                <div className="bg-slate-50 rounded-lg p-4 mb-4">
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Format: Nom,X,Y (un point par ligne)
                  </label>
                  <textarea
                    value={coordText}
                    onChange={(e) => setCoordText(e.target.value)}
                    rows={5}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 font-mono"
                    placeholder="Point A,500000,300000&#10;Point B,500100,300100&#10;Point C,500200,300200"
                  />
                  <button
                    type="button"
                    onClick={addCoordinatesFromText}
                    className="btn-primary mt-2"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Ajouter les points
                  </button>
                </div>
              )}

              {/* Warning */}
              {coordWarning && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center text-yellow-800 text-sm">
                  <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                  {coordWarning}
                </div>
              )}

              {/* Liste des points */}
              {formData.coordonnees.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">
                    Points ajoutés ({formData.coordonnees.length}):
                  </h4>
                  <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg divide-y">
                    {formData.coordonnees.map((coord) => (
                      <div key={coord.id} className="flex items-center justify-between px-4 py-2 hover:bg-slate-50">
                        <div className="flex items-center space-x-4 text-sm">
                          <span className="font-medium text-slate-700">{coord.nom}</span>
                          <span className="text-slate-500">X: {formatNumber(coord.x)}</span>
                          <span className="text-slate-500">Y: {formatNumber(coord.y)}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeCoordinate(coord.id)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tab 3: Observations */}
          {activeTab === 3 && (
            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Notes et observations
                </label>
                <textarea
                  value={formData.observations}
                  onChange={(e) => handleChange('observations', e.target.value)}
                  rows={8}
                  className="field"
                  placeholder="Saisissez vos notes, observations, remarques particulières..."
                />
              </div>

              {/* Aperçu récapitulatif */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Aperçu du Projet</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Client(s):</p>
                    <p className="font-medium">{buildClientLabel(formData.clients) || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Commune:</p>
                    <p className="font-medium">{formData.commune || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Nature:</p>
                    <p className="font-medium">{formData.natureProjet || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Zone:</p>
                    <p className="font-medium">
                      {ZONES_MERCHICH.find(z => z.id === formData.zoneMerchich)?.shortName || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500">Montant Total:</p>
                    <p className="font-medium">{formatNumber(totalGeneral)} DH</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Reste à Payer:</p>
                    <p className={`font-medium ${resteAPayer > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                      {formatNumber(resteAPayer)} DH
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-slate-50">
          <div className="flex items-center">
            {Object.keys(errors).length > 0 && (
              <span className="text-red-500 text-sm flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {Object.keys(errors).length} erreur(s) à corriger
              </span>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onCancel}
              className="btn-secondary"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || Object.keys(errors).length > 0}
              className="btn-primary px-6"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sauvegarde...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Sauvegarder
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectForm;
